/* eslint-disable */
import React from "react";
import {
  Card,
  Group,
  Text,
  Title,
  Tooltip,
  createStyles,
  Button,
} from "@mantine/core";
import {
  BarChart as ReBarChart,
  Bar as ReBar,
  XAxis as ReXAxis,
  YAxis as ReYAxis,
  CartesianGrid as ReCartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer as ReResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useGetDepartmentsWithUsers } from "api/department/getEmployeeDepartment";

const ResponsiveContainerAny =
  ReResponsiveContainer as unknown as React.ComponentType<any>;

const BarChartAny = ReBarChart as unknown as React.ComponentType<any>;
const BarAny = ReBar as unknown as React.ComponentType<any>;
const XAxisAny = ReXAxis as unknown as React.ComponentType<any>;
const YAxisAny = ReYAxis as unknown as React.ComponentType<any>;
const CartesianGridAny = ReCartesianGrid as unknown as React.ComponentType<any>;
const TooltipAny = ReTooltip as unknown as React.ComponentType<any>;

const useStyles = createStyles((theme) => ({
  rightCard: {
    width: "100%",
    height: "100%",
    borderRadius: 8,
    padding: theme.spacing.md,
    display: "flex",
    flexDirection: "column",
  },
  loadMoreWrapper: {
    marginTop: theme.spacing.sm,
    display: "flex",
    justifyContent: "center",
  },
}));

interface ChartData {
  name: string;
  value: number;
}

export default function EmployeeDepartmentStatus() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { data, isLoading, isFetching, error } = useGetDepartmentsWithUsers();

  const departments = (data?.data ?? []) as any[];

  const chartData: ChartData[] = departments.map((d: any) => {
    const name = d?.name ?? d?.departmentName ?? "—";

    const membersCount = Array.isArray(d?.members)
      ? d.members.length
      : Array.isArray(d?.users)
        ? d.users.length
        : Array.isArray(d?.employees)
          ? d.employees.length
          : typeof d?.count === "number"
            ? d.count
            : 0;

    return {
      name,
      value: membersCount,
    };
  });

  const VISIBLE_COUNT = 5;
  const visibleData = chartData.slice(0, VISIBLE_COUNT);
  const showLoadMore = chartData.length > VISIBLE_COUNT;

  const BAR_HEIGHT = 36;
  const chartHeight = Math.max(260, visibleData.length * BAR_HEIGHT + 60);

  const handleLoadMore = () => {
    // Reset the department view to 'department' tab by default
    localStorage.setItem("departmentView", "department");
    navigate("/department");
  };

  return (
    <Card className={classes.rightCard} shadow="sm" radius="md">
      <Group position="apart">
        <Title order={3} style={{ fontSize: "18px" }}>
          Employees By Department
        </Title>
        {showLoadMore && (
          <div className={classes.loadMoreWrapper}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadMore}
            >
              Load more ({chartData.length - VISIBLE_COUNT} more)
            </Button>
          </div>
        )}
      </Group>

      <div style={{ flex: 1, width: "100%", marginTop: 12 }}>
        {isLoading ? (
          <div style={{ padding: 24 }}>Loading departments...</div>
        ) : error ? (
          <div style={{ padding: 24, color: "red" }}>
            Failed to load departments
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ padding: 24 }}>No departments found</div>
        ) : (
          <ResponsiveContainerAny width="100%" height={chartHeight}>
            <BarChartAny
              layout="vertical"
              data={visibleData}
              margin={{ top: 10, right: 24, left: 10, bottom: 10 }}
            >
              <CartesianGridAny strokeDasharray="3 3" vertical={false} />
              <XAxisAny type="number" />
              <YAxisAny
                type="category"
                dataKey="name"
                width={160}
                interval={0}
                tick={({ x, y, payload }: any) => {
                  const label = payload?.value ?? "—";
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <foreignObject x={-150} y={-10} width={150} height={24}>
                        <Tooltip
                          label={label}
                          withArrow
                          position="right"
                          withinPortal
                          multiline
                          transition="pop"
                          transitionDuration={100}
                          zIndex={5000}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: "#333",
                              textAlign: "right",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {label}
                          </div>
                        </Tooltip>
                      </foreignObject>
                    </g>
                  );
                }}
              />

              <TooltipAny cursor={{ fill: "rgba(150, 150, 150, 0.1)" }} />
              <BarAny
                dataKey="value"
                barSize={20}
                radius={[0, 8, 8, 0]}
                fill="#7950f2"
              />
            </BarChartAny>
          </ResponsiveContainerAny>
        )}
      </div>

      <Group spacing={8} mt={12} position="center">
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#7950f2",
          }}
        />
        <Text size="sm" color="dimmed">
          No of Employees {isFetching ? "(updating...)" : ""} per department
        </Text>
      </Group>
    </Card>
  );
}
